import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, FileText, ScrollText, ShieldCheck } from "lucide-react";
import {
  coerceKind,
  termsKindLabel,
  type PlatformTermsKind,
} from "../../api/General/PlatformTerms";
import Button from "../ui/Button";
import { Spinner } from "../ui/Spinner";
import { cn } from "../../utils/cn";
import SafeMarkdown from "./SafeMarkdown";
import type { TermsReader } from "./useTermsReader";

/**
 * TermsView — presentational reader for a single platform-terms kind. It renders
 * an optional header and the scrollable markdown body; the read-gate, fetching,
 * and acceptance all live in `useTermsReader` (passed in via `reader`) so a
 * docked footer CTA (TermsAcceptBar) can share the same state instance.
 *
 * When `embedded`, the scroll body flexes to fill its container (the dialog's
 * DialogBody) instead of pinning to a fixed viewport height — this keeps the
 * footer reachable on short viewports. The standalone card variant keeps a
 * bounded max-height since it has no flex parent to constrain it.
 */

interface TermsViewProps {
  reader: TermsReader;
  /** Drives the header icon/label fallback before data resolves. */
  kind: PlatformTermsKind;
  /** Render inline (no outer card chrome, flex within the dialog body). */
  embedded?: boolean;
  /** Hide the title chrome (when the surrounding shell already has one). */
  hideHeader?: boolean;
}

function KindIcon({ kind }: { kind: PlatformTermsKind }) {
  switch (kind) {
    case "Privacy":
      return <ShieldCheck size={18} />;
    case "TrialUpgrade":
      return <ScrollText size={18} />;
    case "GeneralUsage":
    default:
      return <FileText size={18} />;
  }
}

const TermsView: React.FC<TermsViewProps> = ({
  reader,
  kind,
  embedded = false,
  hideHeader = false,
}) => {
  const { t } = useTranslation();
  const { loading, errorMsg, data, scrollRef, onScroll, retry } = reader;

  const kindKey = useMemo<PlatformTermsKind>(
    () => coerceKind(data?.kind ?? kind),
    [data?.kind, kind],
  );

  return (
    <div
      className={cn(
        "flex flex-col min-h-0",
        // Embedded: flex to fill the (flex) dialog body so the scroll area below
        // can shrink and scroll. Standalone: a bordered card at natural height.
        embedded ? "flex-1" : "rounded-xl border border-border bg-card overflow-hidden",
      )}
    >
      {!hideHeader && (
        <div
          className={cn(
            "flex items-start gap-3",
            embedded ? "mb-3" : "px-5 py-4 border-b border-border bg-muted/30",
          )}
        >
          <div className="shrink-0 w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <KindIcon kind={kindKey} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground truncate">
              {data?.title || t(termsKindLabel(kindKey))}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {data ? (
                <>
                  {t("Version {{v}}", { v: data.version })}
                  {data.effectiveFrom && (
                    <>
                      <span className="mx-1.5 opacity-40">·</span>
                      {t("Effective from")} {new Date(data.effectiveFrom).toLocaleDateString()}
                    </>
                  )}
                </>
              ) : (
                t("Loading...")
              )}
            </div>
          </div>
        </div>
      )}

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className={cn(
          "overflow-y-auto",
          embedded ? "flex-1 min-h-0 px-6 py-5" : "px-5 py-5 max-h-[55vh]",
        )}
      >
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="md" />
          </div>
        )}

        {!loading && errorMsg && (
          <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span className="flex-1">{errorMsg}</span>
            <Button variant="outline" size="sm" onClick={retry}>
              {t("Retry")}
            </Button>
          </div>
        )}

        {!loading && !errorMsg && data && <SafeMarkdown content={data.contentMarkdown} />}
      </div>
    </div>
  );
};

export default TermsView;
